/**
 * useExchangeContext — derives the Exchange Lane state for a cycle so that
 * session creation can surface Catchball context (Canon 23 §9.3, Gap 4).
 *
 * Exchange state semantics (mirrors the scenario matrix dimension):
 *   not_applicable    — no active Catchball loop governs this context
 *   request_received  — an active loop has issued a Request; session work
 *                       responds to that intent
 *   revision_received — the Evaluator returned the proposal with revision
 *                       guidance; session work responds to the revision
 *
 * Ball/decision roles are resolved from live loop state (Doc 05 §13 —
 * never from cycle_participants).
 */

import { useEffect, useMemo } from 'react';
import { useCatchballLoopStore } from '../../stores/prioritization/catchballLoopStore';
import type { CatchballLoop, CatchballLoopState } from '../../types/prioritization/catchballLoop';
import type { LoopIntent } from '../../types/prioritization/catchballLoop';

export type ExchangeState = 'not_applicable' | 'request_received' | 'revision_received';

export interface ExchangeContext {
  exchangeState: ExchangeState;
  /** The active loop driving the exchange state, when one exists. */
  activeLoop: CatchballLoop | null;
  /** The Request intent travelling down with the ball, when set. */
  intent: LoopIntent | null;
  /** True while the loop list is being fetched. */
  loading: boolean;
}

const REVISION_STATES: CatchballLoopState[] = [
  'REVISION_REQUESTED',
  'revision_requested',
  'returned',
];

const TERMINAL_STATES: CatchballLoopState[] = [
  'ACCEPTED',
  'FINAL_REJECTED',
  'DECLINED',
  'closed',
  'rejected',
  'output_ready',
];

export function deriveExchangeState(loop: CatchballLoop | null | undefined): ExchangeState {
  if (!loop) return 'not_applicable';
  if (TERMINAL_STATES.includes(loop.state)) return 'not_applicable';
  if (REVISION_STATES.includes(loop.state)) return 'revision_received';
  // Any other non-terminal loop with an issued Request means downstream work
  // is responding to executive intent.
  if (loop.intent || loop.requestIssuedAt) return 'request_received';
  return 'not_applicable';
}

export function useExchangeContext(cycleId: string | null | undefined): ExchangeContext {
  const fetchFromSupabase = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const getCurrentLoop = useCatchballLoopStore((s) => s.getCurrentLoop);
  const loops = useCatchballLoopStore((s) => s.loops);
  const loading = useCatchballLoopStore((s) => (s as { loading?: boolean }).loading ?? false);

  useEffect(() => {
    if (cycleId) void fetchFromSupabase(cycleId);
  }, [cycleId, fetchFromSupabase]);

  return useMemo(() => {
    if (!cycleId) {
      return { exchangeState: 'not_applicable' as const, activeLoop: null, intent: null, loading: false };
    }
    // `loops` in deps keeps this current as the store hydrates.
    void loops;
    const activeLoop = getCurrentLoop(cycleId) ?? null;
    const exchangeState = deriveExchangeState(activeLoop);
    return {
      exchangeState,
      activeLoop: exchangeState === 'not_applicable' ? null : activeLoop,
      intent: exchangeState === 'not_applicable' ? null : activeLoop?.intent ?? null,
      loading,
    };
  }, [cycleId, loops, getCurrentLoop, loading]);
}
